import useCreateCompany from '@/modules/leadIndication/hooks/useCreateCompany';
import useIndicationSheetRows from '@/modules/leadIndication/hooks/useIndicationSheetRows';
import useLeadIndicationColumns from '@/modules/leadIndication/hooks/useLeadIndicationColumns';
import useLeadIndicationDownload from '@/modules/leadIndication/hooks/useLeadIndicationDownload';
import useLeadIndicationPermissions from '@/modules/leadIndication/hooks/useLeadIndicationPermissions';
import useReferringCompanies from '@/modules/leadIndication/hooks/useReferringCompanies';
import useUploadIndication from '@/modules/leadIndication/hooks/useUploadIndication';
import React from 'react';
import { Button } from 'vkit/lib/components';

/**
 * Painel temporário de evidência dos hooks do módulo de indicações.
 *
 * Instancia manualmente cada hook, loga o objeto retornado e expõe botões que
 * disparam suas ações, servindo apenas como ferramenta de teste manual.
 *
 * TODO: componente temporário de evidência — remover antes do commit.
 */
const HooksEvidencePanel: React.FC = () => {
  const permissions = useLeadIndicationPermissions();
  const referringCompanies = useReferringCompanies();
  const leadIndicationColumns = useLeadIndicationColumns({
    onDownload: (indication) => console.log('[evidencia] onDownload chamado com:', indication),
  });
  const leadIndicationDownload = useLeadIndicationDownload();
  const uploadIndication = useUploadIndication();
  const createCompany = useCreateCompany();
  const indicationSheetRows = useIndicationSheetRows({ file: null, maxRows: 10 });

  console.log('[evidencia] useLeadIndicationPermissions:', permissions);
  console.log('[evidencia] useReferringCompanies:', referringCompanies);
  console.log('[evidencia] useLeadIndicationColumns:', leadIndicationColumns);
  console.log('[evidencia] useLeadIndicationDownload:', leadIndicationDownload);
  console.log('[evidencia] useUploadIndication:', uploadIndication);
  console.log('[evidencia] useCreateCompany:', createCompany);
  console.log('[evidencia] useIndicationSheetRows:', indicationSheetRows);

  const handleReferringCompaniesReload = () => {
    console.log('[evidencia] useReferringCompanies.refreshData() disparado');
    referringCompanies.refreshData();
  };

  const handleReferringCompaniesNextPage = () => {
    console.log('[evidencia] useReferringCompanies.goToNextPage() disparado', {
      hasNextPage: referringCompanies.hasNextPage,
      endCursor: referringCompanies.endCursor,
    });
    referringCompanies.goToNextPage();
  };

  const handleReferringCompaniesPreviousPage = () => {
    console.log('[evidencia] useReferringCompanies.goToPreviousPage() disparado', {
      hasPreviousPage: referringCompanies.hasPreviousPage,
      startCursor: referringCompanies.startCursor,
    });
    referringCompanies.goToPreviousPage();
  };

  const handleCreateCompany = async () => {
    const payload = {
      name: 'Empresa Evidência LTDA',
      cnpj: '11222333000181',
    };
    console.log('[evidencia] useCreateCompany.createCompany() payload:', payload);
    const response = await createCompany.createCompany(payload);
    console.log('[evidencia] useCreateCompany.createCompany() resultado:', response);
  };

  const handleUploadIndication = async () => {
    const file = new File(['coluna1,coluna2\nvalor1,valor2'], 'evidencia.csv', {
      type: 'text/csv',
    });
    const payload = {
      file,
      referringCompanyId: 'evidencia-company-id',
      referringCompanyName: 'Empresa Evidência LTDA',
      referringCompanyCnpj: '11222333000181',
    };
    console.log('[evidencia] useUploadIndication.uploadIndication() payload:', payload);
    try {
      const response = await uploadIndication.uploadIndication(payload);
      console.log('[evidencia] useUploadIndication.uploadIndication() resultado:', response);
    } catch (error) {
      console.log('[evidencia] useUploadIndication.uploadIndication() erro:', error);
    }
  };

  const handleDownload = async () => {
    const indication = {
      id: 'evidencia-indication-id',
      referringCompany: 'Empresa Evidência LTDA',
      cnpj: '11222333000181',
      indicatedAt: new Date().toISOString(),
      indicatedBy: 'Usuário Evidência',
      fileId: 'evidencia-file-id',
      fileCompanyId: 'evidencia-company-id',
    };
    console.log('[evidencia] useLeadIndicationDownload.download() indication:', indication);
    const result = await leadIndicationDownload.download(indication);
    console.log('[evidencia] useLeadIndicationDownload.download() resultado:', result);
  };

  const handleReadSheet = async () => {
    const file = new File(
      ['nome,email\nFulano,fulano@teste.com\nBeltrano,beltrano@teste.com'],
      'evidencia.csv',
      { type: 'text/csv' },
    );
    console.log('[evidencia] useIndicationSheetRows.readFile() file:', file);
    const info = await indicationSheetRows.readFile(file);
    console.log('[evidencia] useIndicationSheetRows.readFile() resultado:', info);
  };

  return (
    <div
      style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '16px 0' }}
      data-testid='evidencia-hooks-panel'>
      <Button
        label='Evidência: recarregar empresas indicadoras'
        onClick={handleReferringCompaniesReload}
      />
      <Button
        label='Evidência: próxima página (empresas)'
        onClick={handleReferringCompaniesNextPage}
        disabled={!referringCompanies.hasNextPage}
        loading={referringCompanies.loading}
      />
      <Button
        label='Evidência: página anterior (empresas)'
        onClick={handleReferringCompaniesPreviousPage}
        disabled={!referringCompanies.hasPreviousPage}
        loading={referringCompanies.loading}
      />
      <Button
        label='Evidência: criar empresa'
        onClick={handleCreateCompany}
        loading={createCompany.loading}
      />
      <Button
        label='Evidência: upload indicação'
        onClick={handleUploadIndication}
        loading={uploadIndication.loading}
      />
      <Button label='Evidência: download arquivo' onClick={handleDownload} />
      <Button
        label='Evidência: ler planilha'
        onClick={handleReadSheet}
        loading={indicationSheetRows.loading}
      />
    </div>
  );
};

export default HooksEvidencePanel;
